import type { OrderTimelineResponseDto } from "Infrastructure/Api/Api";

export type ActivePartnerInfo = {
	partnerId: string;
	/** ISO date string of when this partner was assigned */
	assignedAt: string;
};

/**
 * Walks through timeline entries (newest-first from the API) and determines
 * which partner was assigned to the order at the time of each entry.
 *
 * Returns a Map of timeline-entry id -> { partnerId, assignedAt } that was
 * active when that entry was created.
 */
export function deriveActivePartnerMap(
	entries: OrderTimelineResponseDto[],
): Map<number, ActivePartnerInfo> {
	// The API returns entries sorted by dateCreated DESC (newest first).
	// We reverse to process chronologically so we can track the "current" partner.
	const chronological = [...entries].reverse();

	const result = new Map<number, ActivePartnerInfo>();
	let currentPartner: ActivePartnerInfo | null = null;

	for (const entry of chronological) {
		// When we see a partnerId change, update the tracked partner
		if (entry.key === "partnerId") {
			if (entry.valueTo) {
				currentPartner = {
					partnerId: entry.valueTo,
					assignedAt: entry.dateCreated,
				};
			} else {
				// Partner was unassigned — clear so later entries don't
				// incorrectly show the previous partner
				currentPartner = null;
			}
		}

		// Record the active partner for every entry
		if (currentPartner) {
			result.set(entry.id, currentPartner);
		}
	}

	return result;
}

/**
 * Builds a chronological list of partner assignments from timeline entries.
 * Returns a lookup function: given an ISO timestamp, it returns the
 * ActivePartnerInfo that was effective at that point in time (or null).
 */
export function buildPartnerLookupByTime(
	entries: OrderTimelineResponseDto[],
): (targetTime: string) => ActivePartnerInfo | null {
	const chronological = [...entries].reverse();

	// Build ordered list of partner change events
	const changes: { time: Date; partner: ActivePartnerInfo | null }[] = [];

	for (const entry of chronological) {
		if (entry.key === "partnerId") {
			if (entry.valueTo) {
				changes.push({
					time: new Date(entry.dateCreated),
					partner: {
						partnerId: entry.valueTo,
						assignedAt: entry.dateCreated,
					},
				});
			} else {
				changes.push({
					time: new Date(entry.dateCreated),
					partner: null,
				});
			}
		}
	}

	return (targetTime: string): ActivePartnerInfo | null => {
		const target = new Date(targetTime);
		let activePartner: ActivePartnerInfo | null = null;

		for (const change of changes) {
			if (change.time <= target) {
				activePartner = change.partner;
			} else {
				break;
			}
		}

		return activePartner;
	};
}
